import { InternalAxiosRequestConfig } from 'axios';
import pkceChallenge from 'pkce-challenge';
import QueryString from 'qs';
import { isOutletSelectionRoute, isPotentialHybridJourneyRoute, routes } from '../../routing/routes';
import { assertDefinedVoid } from '../common/miscUtils';
import { SetState } from '../common/miscUtils.types';
import { storage } from '../localStorage';
import { staticSelector, store } from '../redux';
import {
  brokerSelectors,
  setAllowedCompanies,
  setCurrentCompany,
  setFavouriteCompanyId,
  setMilesSalesPersonId,
  setUsername,
} from '../redux/features/broker.slice';
import { setIsHybridJourney } from '../redux/features/custom.slice';
import { loadingActions } from '../redux/features/loading.slice';
import { NavigateType } from '../routing/navigate';
import { logout, requestToken, requestTokenFromRefreshToken, RequestTokenResponse } from './auth';
import { getAllowedBrokerCompanies, getBrokerLoginLink, getCurrentDealerContacts } from './miles/miles-services';

let _navigate = {} as unknown as NavigateType;

const getCodeChallenge = async () => {
  const challenge = await pkceChallenge(100);

  storage.auth.codeChallenge = challenge.code_challenge;
  storage.auth.codeVerifier = challenge.code_verifier;

  return challenge.code_challenge;
};

const getRedirectUri = () => {
  // Url to which Miles login page will redirect user after successful login.
  const returnUrl = isPotentialHybridJourneyRoute()
    ? window.location.href // Keep full URLs in case of later journey stage
    : window.origin; // e.g.: https://vwfs.int.fr.pos.vwfs.io/

  storage.auth.redirectUri = returnUrl;

  return encodeURIComponent(returnUrl);
};

const createRedirectUrl = async () => {
  const stage = process.env.REACT_APP_STAGE;
  const stageFragment = stage === 'prod' ? '' : `-${stage}`;

  const baseLoginUrl = `https://fr${stageFragment}.miles.vwfs.com/auth/realms/master/protocol/openid-connect/auth`;
  const responseType = '?response_type=code';
  const clientId = `&client_id=vwfr-frontend-api-client`;
  const redirectUri = `&redirect_uri=${getRedirectUri()}`;
  const codeChallenge = `&code_challenge=${await getCodeChallenge()}`;
  const codeChallengeMethod = `&code_challenge_method=S256`;

  return baseLoginUrl + responseType + clientId + redirectUri + codeChallenge + codeChallengeMethod;
};

const removeAuthorizationDataFromUrl = () => {
  const cleanUrl = window.location.origin;
  window.history.replaceState(null, '', cleanUrl);
};

const getParameterFromUrl = (parameterName: 'code' | 'access_token'): string | undefined => {
  const query = window.location.search || window.location.hash;
  if (query === '') return undefined;

  const parsedUrl = QueryString.parse(query);
  const parameterValue = parsedUrl[parameterName] as string;
  return parameterValue ?? undefined;
};

const getAuthorizationCodeFromUrl = (): string | undefined => {
  return getParameterFromUrl('code');
};

const getAccessTokenFromUrl = (): string | undefined => {
  return getParameterFromUrl('access_token');
};

const storeTokenData = (response: RequestTokenResponse) => {
  storage.auth.milesTokenValue = response.access_token;
  storage.auth.milesRefreshTokenValue = response.refresh_token;
  storage.auth.milesTokenExpirationDate = Date.now() + response.expires_in * 1000;
  storage.auth.milesRefreshTokenExpirationDate = Date.now() + response.refresh_expires_in * 1000;
};

export const redirectToLoginPage = async () => {
  const redirectUrl = await createRedirectUrl();
  window.location.href = redirectUrl;
  return new Promise(() => {
    /* waiting for redirect to take effect */
  });
};

let getAndSaveTokenPromise = Promise.resolve();
let isTokenPending = false;

export const getAndSaveToken = async ({ refresh, initialCheck }: { refresh?: boolean; initialCheck?: boolean }) => {
  isTokenPending = true;
  let _resolve;
  let _reject;
  const promise = new Promise<void>((resolve, reject) => {
    _resolve = resolve;
    _reject = reject;
  });

  getAndSaveTokenPromise = promise;

  storage.auth.milesTokenValue = undefined;

  let response: RequestTokenResponse;

  if (refresh) {
    const refreshToken = storage.auth.milesRefreshTokenValue;

    if (!refreshToken) {
      isTokenPending = false;
      _reject();
      return promise;
    }

    response = await requestTokenFromRefreshToken(refreshToken);
  } else {
    const code = getAuthorizationCodeFromUrl();
    removeAuthorizationDataFromUrl();
    const { codeVerifier, redirectUri } = storage.auth;

    if (!code || !codeVerifier || !redirectUri) {
      isTokenPending = false;
      _reject();
      return promise;
    }

    try {
      response = await requestToken(code, codeVerifier, redirectUri);
    } catch {
      if (initialCheck) {
        throw new Error('Initial login failed');
      } else {
        return _navigate.error();
      }
    }
  }

  storeTokenData(response);
  // eslint-disable-next-line no-use-before-define
  await scheduleTokenRefresh();

  isTokenPending = false;
  _resolve();

  return promise;
};

let scheduledTokenRefreshTimeout: number = -1;

// eslint-disable-next-line no-use-before-define
export const scheduleTokenRefresh = async () => {
  if (scheduledTokenRefreshTimeout !== -1) {
    window.clearTimeout(scheduledTokenRefreshTimeout);
  }

  const milliseconds = storage.auth.milesTokenExpirationDate! - Date.now();

  if (milliseconds <= 0) {
    await getAndSaveToken({ refresh: true });
  }

  scheduledTokenRefreshTimeout = window.setTimeout(() => {
    getAndSaveToken({ refresh: true });
  }, milliseconds);
};

const isAuthorizationDataPresent = () => {
  return !Object.keys(storage.auth).some((key) => !storage.auth[key]);
};

/**
 * - If the liftime left to the token at the moment of any authenticated requst
 * will be lower than `buffer`, then we will try to refresh.
 */
const buffer = 30000;

const isTokenValid = () => {
  return Date.now() < storage.auth.milesTokenExpirationDate! - buffer;
};

const isRefreshTokenValid = () => {
  return Date.now() < storage.auth.milesRefreshTokenExpirationDate!;
};

export const isValidAuthorizationDataPresent = () => {
  return isAuthorizationDataPresent() && (isTokenValid() || isRefreshTokenValid());
};

const isAuthorizationCodeInUrl = () => !!getAuthorizationCodeFromUrl();
const isAccessTokenInUrl = () => !!getAccessTokenFromUrl();

export const removeAuthorizationData = () => {
  Object.keys(storage.auth).forEach((key) => {
    storage.auth[key] = undefined;
  });
};

const validateOrObtainMilesToken = async () => {
  if (isTokenPending) await getAndSaveTokenPromise.catch(/* ignore error */);

  if (isAuthorizationDataPresent() && isTokenValid()) return true;

  if (isRefreshTokenValid()) {
    await getAndSaveToken({ refresh: true });
    return true;
  }

  removeAuthorizationData();
  await redirectToLoginPage();

  return false;
};

export const decorateAxiosRequestConfigWithToken = async (config: InternalAxiosRequestConfig) => {
  const result = await validateOrObtainMilesToken();

  if (!result) {
    // If result was not truthy then we want to wait for the redirect to take the effect (it's not instantaneous).
    // For that we wait for an unresolvable promise.
    await new Promise(() => {
      /*  */
    });
  }

  const token = storage.auth.milesTokenValue;

  config.headers.Authorization = token;

  return config;
};

export const updateRouterNavigationRefForAuth = (navigate: NavigateType) => {
  _navigate = navigate;
};

export const logoutAndRemoveAuthorizationData = async () => {
  const refreshToken = storage.auth.milesRefreshTokenValue;

  if (refreshToken) {
    await logout(refreshToken);
  }

  removeAuthorizationData();
};

/**
 * @param manual - Indicates if login as company is initialized by the user.
 */
export const loginAsOutlet = async (milesCompanyId: string, manual?: boolean) => {
  const authenticatedMilesCompany = staticSelector(brokerSelectors.currentCompany);
  const authenticatedMilesCompanyId = authenticatedMilesCompany?.milesCompanyId;

  if (!authenticatedMilesCompanyId || authenticatedMilesCompanyId !== milesCompanyId) {
    if (manual) {
      storage.manualLogInAsCompanyInProgress = true;
    }

    const loadingId = store.dispatch(loadingActions.startContextualLoading()).payload.id;
    const redirectUrl = (await getBrokerLoginLink(milesCompanyId!)).url;

    try {
      await logoutAndRemoveAuthorizationData();
      window.location.href = redirectUrl;
    } catch {
      store.dispatch(loadingActions.endContextualLoading(loadingId));
      _navigate.error();
    }
  } else {
    _navigate.dashboardHome();
  }
};

const setupSalesPerson = async () => {
  const dealerContacts = await getCurrentDealerContacts();

  const salesPerson = dealerContacts.dealers?.[0].salesPerson;
  const username = salesPerson?.username;

  assertDefinedVoid(username, 'Salesperson username is not defined');

  store.dispatch(setUsername(username));

  const milesSalesPersonId = salesPerson?.milesSalesPersonId;
  if (milesSalesPersonId) {
    store.dispatch(setMilesSalesPersonId(milesSalesPersonId));
  }

  const { dealers } = await getAllowedBrokerCompanies();

  const companies = dealers?.[0]?.salesPerson?.milesBrokerCompanies?.map((company) => ({ ...company })) ?? [];

  store.dispatch(setAllowedCompanies(companies));

  const selectedCompanyFromContacts = dealerContacts.dealers?.[0].salesPerson.milesBrokerCompanies?.[0];

  assertDefinedVoid(selectedCompanyFromContacts, 'Current salesperson has no companies');

  const selectedCompany =
    companies.find((company) => company.milesCompanyId === selectedCompanyFromContacts.milesCompanyId) ??
    selectedCompanyFromContacts;

  store.dispatch(setCurrentCompany(selectedCompany));
};

/**
 * - In case of return to dealer-only part of the POS in hybrid journey
 * restore selected company and un-mark journey as hybrid.
 */
export const resumeDealerJourney = async () => {
  if (!isValidAuthorizationDataPresent()) {
    window.location.href = `${window.location.origin}${routes.root}`;
    await new Promise(() => {
      /* block any possible microtask following the redirect */
    });
  }

  await scheduleTokenRefresh();

  store.dispatch(setIsHybridJourney(false));

  store.dispatch(loadingActions.setLoading(true));

  await setupSalesPerson().finally(() => {
    store.dispatch(loadingActions.setLoading(false));
  });
};

export const checkTokenOnApplicationLoad = async () => {
  if (!isValidAuthorizationDataPresent()) {
    if (isPotentialHybridJourneyRoute()) {
      if (isAuthorizationDataPresent()) {
        // Presumed dealer.
        // Realistically it's not possible that non-dealer will
        // just randomly have various localStorage entries related to our auth flow.
        removeAuthorizationData();
        await redirectToLoginPage();
        return;
        // eslint-disable-next-line no-else-return
      } else {
        // Almost certainly not a dealer.
        // Even if dealer - it's a weird situation that they do not have
        // auth flow related fields in storage.
        // Should be rare occurrence.
        return;
      }
    }

    if (isAuthorizationCodeInUrl()) {
      await getAndSaveToken({ initialCheck: true });
      await setupSalesPerson();
      return;
    }

    if (isAccessTokenInUrl()) {
      // Go back to login page to undergo automatic authentication to obtain refresh token
      await redirectToLoginPage();
      return;
    }

    removeAuthorizationData();
    await redirectToLoginPage();

    return;
  }

  await scheduleTokenRefresh();

  await setupSalesPerson();
};

/**
 * - Check if should login as favorite company and initiate login if needed.
 */
export const checkLoginAsFovoriteCompany = (setLoginAsFavouriteDone: SetState<boolean>) => {
  if (!isValidAuthorizationDataPresent() || !isOutletSelectionRoute()) {
    setLoginAsFavouriteDone(true);
    return;
  }

  const favouriteMilesCompanyId = storage.favouriteCompanyId;
  const allowedCompanies = staticSelector(brokerSelectors.allowedCompanies);
  const isFavouriteCompanyAllowed = allowedCompanies.some(
    (company) => company.milesCompanyId === favouriteMilesCompanyId
  );

  store.dispatch(setFavouriteCompanyId(favouriteMilesCompanyId));

  if (storage.manualLogInAsCompanyInProgress) {
    storage.manualLogInAsCompanyInProgress = false;
    setLoginAsFavouriteDone(true);
    _navigate.dashboardHome();
  } else {
    const currentCompany = staticSelector(brokerSelectors.currentCompany);

    if (
      isFavouriteCompanyAllowed &&
      favouriteMilesCompanyId &&
      currentCompany?.milesCompanyId !== favouriteMilesCompanyId
    ) {
      loginAsOutlet(favouriteMilesCompanyId);
    } else {
      setLoginAsFavouriteDone(true);
    }
  }
};
